import React, { Component } from 'react'
import { withFirebase } from '../../components/Fb'
import { Row, Col, Button } from 'reactstrap'
import { DataGrid } from '@mui/x-data-grid'
import FalconModal from '../../components/Inputs/FalconModal'
import { getRecurringSaleSchema, getRecurringSaleUISchema } from '../../components/Inputs/dataschemas'
import EditButton from '../../components/Buttons/EditButton'
import DeleteButton from '../../components/Buttons/DeleteButton'
import CalendarHeatmap from 'react-calendar-heatmap'
import moment from 'moment'
import 'react-calendar-heatmap/dist/styles.css'

const enumerateDates = (startDate, endDate) => {
  const dates = []
  const currDate = moment(startDate).startOf('day')
  const lastDate = moment(endDate).startOf('day')

  while (currDate.add(1, 'days').diff(lastDate) < 0) {
    dates.push(currDate.clone())
  }

  return dates
}

// Must stay in step with payment.oneOf in schemas/recurringsale.js - the grid renders
// labels from here, the form offers options from there.
const PAYMENT_TYPES = {
  postpaidcreditcard: 'Jälkikäteen luottokortti',
  prepaidfull: 'Etukäteen kokokausi',
  prepaidpartly: 'Etukäteen osakausi',
  postpaidinvoice: 'Jälkikäteen lasku',
  inquiry: 'Tiedustelu',
}

const CONTRACT_TYPES = {
  creditcard: 'Talletettu luottokortti',
  esignature: 'Sähköinen allekirjoitus',
  prepaid: 'Maksu etukäteen',
  none: 'Ei mitään'
}

class RecurringSalesContainer extends Component {
  constructor(props) {
    super(props)
    this.state = {
      rows: [],
      loading: true,
      saleModalOpen: false,
      selectedResource: {},
      images: [],
      heatdates: [],
      pricings: [],
    }
  }

  componentDidMount() {
    this.props.firebase.listenRecurringSales().onSnapshot((snapshot) => {
      if (!snapshot.empty) {
        const rows = snapshot.docs.map((doc) => {
          return { ...doc.data(), id: doc.id }
        })
        .filter((x) => x.state !== 'deleted')
        const heatdates = rows
          .map((x) => {
            const dates = enumerateDates(x.campaignstart, x.campaignend)
            return dates.map((d) => {
              return { date: d.format('YYYY-MM-DD'), count: 100, id: x.id }
            })
          })
          .flat()
        this.setState({ rows, heatdates })
      }
    })
    this.props.firebase.getRecurringSaleResourcePricings().then((values) => {
      if (!values.empty) {
        let _d = values.docs
          .map((sh) => {
            return { ...sh.data(), id: sh.id }
          })
          .sort((a, b) => {
            return (b.priority || 0) - (a.priority || 0)
          })
        this.setState({ pricings: _d })
      }
    })
  }

  getColDefinitions = () => {
    const cols = [
      {
        headerName: 'Nimi',
        field: 'name',
        disableClickEventBubbling: true,
      },
      {
        headerName: 'Kampanjan alku', 
        field: 'campaignstart',
        disableClickEventBubbling: true,
        valueFormatter: (value) => {
          return new Date(value).toLocaleDateString()
        },
      },
      {
        headerName: 'Kampanjan loppu',
        field: 'campaignend',
        disableClickEventBubbling: true,
        valueFormatter: (value) => {
          return new Date(value).toLocaleDateString()
        },
      },
      {
        headerName: 'Kauden alku',
        field: 'seasonstart',
        disableClickEventBubbling: true,
        valueFormatter: (value) => {
          return new Date(value).toLocaleDateString()
        },
      },
      {
        headerName: 'Kauden loppu',
        field: 'seasonend',
        disableClickEventBubbling: true,
        valueFormatter: (value) => {
          return new Date(value).toLocaleDateString()
        },
      },
      {
        headerName: 'Maksu',
        field: 'payment',
        disableClickEventBubbling: true,
        valueFormatter: (value) => {
          return PAYMENT_TYPES[value]
        }
      },
      {
        headerName: 'Sopimus',
        field: 'contract',
        disableClickEventBubbling: true,
        valueFormatter: (value) => {
          return CONTRACT_TYPES[value]
        }
      },
      {
        headerName: 'Tila',
        field: 'state',
        disableClickEventBubbling: true,
        valueFormatter: (value) => {
          return value === 'draft' ? 'Luonnos' : value === 'active' ? 'Aktiivinen' : 'Keskeytetty'
        },
      },
      {
        headerName: 'Muokkaa',
        field: 'edit',
        disableClickEventBubbling: true,
        renderCell: (params) => {
          return <EditButton onClick={() => this.handleRowSelect(params.row.id)} />
        },
      },
      {
        headerName: 'Poista',
        field: 'delete',
        disableClickEventBubbling: true,
        renderCell: (params) => {
          return <DeleteButton onClick={() => this.deleteSaleDocument(params.row.id)} />
        },
      },
    ]
    return cols.map((x) => {
      return { ...x, flex: 1 / cols.length }
    })
  }

  updateSaleDocument = async (data) => {
    Object.keys(data).forEach(key => { if (data[key] === undefined) { data[key] = '' }})
    await this.props.firebase.updateRecurringSale(data)
  }

  createSaleDocument = async (data) => {
    Object.keys(data).forEach(key => data[key] === undefined && delete data[key])
    await this.props.firebase.createRecurringSale(data)
  }

  getEnhancedSchema = () => {
    const schema = getRecurringSaleSchema()
    console.log(schema)
    const pricing = { ...schema.properties.pricing }
    if (this.state.pricings) {
      for (let i = 0; i < this.state.pricings.length; i++) {
        pricing.oneOf.push({ const: this.state.pricings[i].id, title: this.state.pricings[i].name })
      }
    }
    return schema
  }

  createSaleModal = () => {
    return (
      <FalconModal
        onUpdate={this.updateSaleDocument}
        onCreate={this.createSaleDocument}
        modifiedCallback={this.updateData}
        toggle={this.toggleSaleModal}
        resource={this.state.selectedResource}
        isOpen={this.state.saleModalOpen}
        uiSchema={getRecurringSaleUISchema()}
        dataSchema={this.getEnhancedSchema()}
        title={this.state.selectedResource && this.state.selectedResource.id ? 'Muokkaa myyntiä' : 'Uusi myynti'}
      />
    )
  }

  addNewSaleDocument = () => {
    this.setState({ selectedResource: {} }, this.toggleSaleModal)
  }

  deleteSaleDocument = async (id) => {
    await this.props.firebase.deleteRecurringSale(id)
  }

  handleRowSelect = (e) => {
    const _result = this.state.rows.filter((r) => r.id === e)
    this.setState({ selectedResource: _result[0] }, this.toggleSaleModal)
  }

  updateData = () => {}

  toggleSaleModal = () => {
    this.setState({
      saleModalOpen: !this.state.saleModalOpen,
    })
  }

  render() {
    return (
      <>
        <div className='content'>
          <Row>
            <Col xs={12} md={3}>
              <Button onClick={this.addNewSaleDocument}>Lisää uusi myyntikampanja</Button>
            </Col>
            <Col xs={12} md={3} className='pb-4'>
              <CalendarHeatmap
                startDate={moment().startOf('month').toDate()}
                endDate={moment().add(6, 'months').toDate()}
                onClick={(value) => {
                  this.handleRowSelect(value.id)
                }}
                values={this.state.heatdates}
                monthLabels={['Tam', 'Hel', 'Maa', 'Huh', 'Tou', 'Kes', 'Hei', 'Elo', 'Syy', 'Lok', 'Mar', 'Jou']}
              />
            </Col>
            <Col>
              
            </Col>
          </Row>
          <Row>
            <Col md='12'>
              <div style={{ display: 'flex', minHeight: 'calc(100vh - 200px)', backgroundColor: '#FFFFFF' }}>
                <div style={{ flexGrow: 1 }}>
                  <DataGrid rows={this.state.rows} columns={this.getColDefinitions()} density='compact' />
                </div>
              </div>
            </Col>
          </Row>
          {this.createSaleModal()}
        </div>
      </>
    )
  }
}
export default withFirebase(RecurringSalesContainer)
